import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { usePartNumberSort } from './usePartNumberSort';
import SubTabToolbar from './SubTabToolbar';
import { useSubTabControls } from './useSubTabControls';

interface SALCommittedItem {
  committed_date: string;
  part_number: string;
  description: string;
  committed_qty: number;
  price_after_discount: number | null;
  row_total: number | null;
  document_total: number | null;
  warehouse_code: string;
  pick_list_id: string;
  [key: string]: unknown;
}

interface SALCommittedSubTabProps {
  partNumber?: string;
}

export default function SALCommittedSubTab({ partNumber }: SALCommittedSubTabProps) {
  const [items, setItems] = useState<SALCommittedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { pageSize, setPageSize, searchInput, setSearchInput, search, page, setPage } = useSubTabControls(partNumber);
  const { ordering, icon: sortIcon, toggle: togglePartNumberSort } = usePartNumberSort(() => setPage(1));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
      if (search) params['search'] = search;
      if (ordering) params['ordering'] = ordering;
      const res = await callOffAPI.getSalCommitted(params);
      const data = res.data as { results?: SALCommittedItem[]; items?: SALCommittedItem[]; count?: number; total_count?: number };
      setItems(data.results || data.items || []);
      setTotalCount(data.count || data.total_count || 0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load SAL committed data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div>
      <SubTabToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No SAL committed data available.</p>
      ) : (
      <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-2 font-medium whitespace-nowrap">Row Delivery Date</th>
              <th
                className="text-left p-2 font-medium whitespace-nowrap cursor-pointer hover:bg-muted/70 select-none"
                onClick={togglePartNumberSort}
              >
                <div className="flex items-center gap-1">Part Number {sortIcon}</div>
              </th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Description</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Remaining Open Quantity</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Price after Discount</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Row Total</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Document Total</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Warehouse Code</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Pick List ID Number</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-b hover:bg-muted/30">
                <td className="p-2 whitespace-nowrap">{item.committed_date}</td>
                <td className="p-2 font-mono text-xs whitespace-nowrap">{item.part_number}</td>
                <td className="p-2">{item.description}</td>
                <td className="p-2 text-right">{item.committed_qty}</td>
                <td className="p-2 text-right">{item.price_after_discount ?? ''}</td>
                <td className="p-2 text-right">{item.row_total ?? ''}</td>
                <td className="p-2 text-right">{item.document_total ?? ''}</td>
                <td className="p-2 whitespace-nowrap">{item.warehouse_code}</td>
                <td className="p-2 whitespace-nowrap">{item.pick_list_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
